import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import GlobalSearch from '../../../components/sharedChild/grid/globalSearch';
import gridOptionsConfig from '../../../components/sharedChild/grid/gridOptionsConfig';
import Loader from '../../../shared/component/Loader';
import { isLoadingByKey } from '../../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore } from '../../../redux/actions';
import { getGridDataByModuleKey, getPolicyQuoteList, getUser } from '../../../redux/selector';
import { policyQuoteInputData } from '../../../shared/constants';
import { DateFormatter, DateTimeFormatter } from '../../../shared/controls/formatters';

const MODULE_KEY = 'policyQuoteList';

const yesNoFormatter = (params) => {
  if (params.value === true || params.value === 'Yes') return 'Yes';
  if (params.value === false || params.value === 'No') return 'No';
  return '';
};

const DATE_ONLY_COLUMNS = [
  ['effectiveDate', 'Effective Date'],
  ['expiryDate', 'Expiry Date'],
  ['indicationDate', 'Indication Date'],
  ['submissionDate', 'Submission Date'],
  ['quoteDate', 'Quote Date'],
  ['bindRequestDate', 'Bind Request Date'],
  ['inceptionDate', 'Inception Date'],
  ['cancelDate', 'Cancel Date'],
  ['renewalDate', 'Renewal Date'],
  ['reinstateDate', 'Reinstate Date'],
  ['endorsementDate', 'Endorsement Date'],
];

const PolicyQuoteList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const gridRef = useRef();

  const stateData = useSelector((state) => state);
  const loading = isLoadingByKey(stateData, MODULE_KEY);
  const rowData = useSelector(getPolicyQuoteList);
  const userInfo = useSelector(getUser);
  // Prefer the states the user picked/kept checked in the "Policy & Quote" modal; fall back to
  // their full assigned list (userVM record visibility) if the page was reached without that state.
  const lobState =
    location.state?.lobState || userInfo?.user?.recordVisibility?.lobState || '';
  const gridOptions = gridOptionsConfig(MODULE_KEY, navigate);
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);

  // Re-fetched fresh on every visit (rather than reusing whatever the store already has) since the
  // selected LOB state(s) can differ each time the user comes from the modal.
  useEffect(() => {
    dispatch(
      fetchDataAndUpdateStore({
        key: MODULE_KEY,
        inputData: {
          ...policyQuoteInputData,
          generalRecordVisibility: 'All',
          lobState,
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobState]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 130,
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      { headerName: 'Control Number', field: 'controlNumber' },
      { headerName: 'Quote Number', field: 'quoteNumber' },
      { headerName: 'Policy Number', field: 'policyNumber' },
      { headerName: 'Transaction ID', field: 'transactionID' },
      { headerName: 'Submission Number', field: 'submissionNumber' },
      { headerName: 'Indication Number', field: 'indicationNumber' },
      { headerName: 'Endorsement Number', field: 'endorsementNumber' },
      { headerName: 'Quote Type', field: 'quoteType' },
      { headerName: 'Source', field: 'source' },
      { headerName: 'LOB', field: 'lobID' },
      { headerName: 'LOB State', field: 'lobState' },
      { headerName: 'State', field: 'stateID' },
      { headerName: 'Stage', field: 'stageID' },
      { headerName: 'Processing Type', field: 'processingTypeID' },
      { headerName: 'Carrier', field: 'carrierID' },
      { headerName: 'Agent', field: 'agentID' },
      { headerName: 'Inforce', field: 'inforce' },
      { headerName: 'Premium', field: 'premium', width: 130 },
      { headerName: 'Indication Version', field: 'indicationVersion' },
      { headerName: 'Quote Version', field: 'quoteVersion' },
      { headerName: 'Policy Version', field: 'policyVersion' },
      ...DATE_ONLY_COLUMNS.map(([field, headerName]) => ({
        headerName,
        field,
        valueFormatter: DateFormatter,
      })),
      { headerName: 'Open Task', field: 'isOpenTask', valueFormatter: yesNoFormatter },
      { headerName: 'Cancelled', field: 'isCancelled', valueFormatter: yesNoFormatter },
      { headerName: 'Closed', field: 'isClosed', valueFormatter: yesNoFormatter },
      { headerName: 'Error', field: 'errorMsg' },
      { headerName: 'Created By', field: 'createdBy' },
      { headerName: 'Created Date', field: 'createdDateTime', valueFormatter: DateTimeFormatter },
      { headerName: 'Updated By', field: 'updatedBy' },
      { headerName: 'Updated Date', field: 'updatedDateTime', valueFormatter: DateTimeFormatter },
    ],
    []
  );

  return (
    <div className="inner-page" style={containerStyle}>
      <div className="dash-container">
        <div className="tbl_container">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-12">
              <div className="main-hd">
                <div className="pagetitle">
                  <img
                    src="../assets/images/role-management-icons/left-arrow.png"
                    alt=""
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/quoteAI')}
                  />
                  Policy Quotes{lobState ? ` — ${lobState}` : ''}
                </div>
                <div className="third-div mobView">
                  <div className="dsk">
                    <GlobalSearch moduleName={MODULE_KEY} />
                  </div>
                </div>
              </div>
              <div className="cutome_ag_theme">
                {loading && <Loader />}
                <div
                  className="ag-theme-balham tbl_height"
                  style={{ width: '100%', height: `calc(100vh - 110px)` }}
                >
                  <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    domLayout="fixedHeight"
                    gridOptions={gridOptions}
                    rowSelection="multiple"
                    suppressRowClickSelection={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyQuoteList;
